import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Dream, DreamConfig, PortfolioComparisonData } from '../../types/dreamSimulator';
import { BarChart3, ChevronRight, TrendingUp, Shield, Zap, PieChart } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';

interface PortfolioComparisonProps {
  dream: Dream;
  config: DreamConfig;
  onContinue: () => void;
}

// Asset risk and volatility scores (0-100)
const assetRiskScores = {
  stocks: 80,
  bonds: 40,
  realEstate: 60,
  savings: 20,
  cosechaCapital: 50,
  other: 50
};

// Asset volatility scores (0-100)
const assetVolatilityScores = {
  stocks: 85,
  bonds: 35,
  realEstate: 50,
  savings: 15,
  cosechaCapital: 40,
  other: 50
};

// Asset expected returns (%)
const assetExpectedReturns = {
  stocks: 10,
  bonds: 6,
  realEstate: 8,
  savings: 4,
  cosechaCapital: 20,
  other: 7
};

const PortfolioComparison: React.FC<PortfolioComparisonProps> = ({ dream, config, onContinue }) => {
  // Placeholder portfolio data
  const [portfolioData, setPortfolioData] = useState<PortfolioComparisonData>({
    currentPortfolio: {
      stocks: 30,
      bonds: 30,
      realEstate: 20,
      savings: 20,
      other: 0
    },
    proposedPortfolio: {
      stocks: 20,
      bonds: 20,
      realEstate: 15,
      savings: 15,
      cosechaCapital: 30,
      other: 0
    },
    riskProfile: {
      currentRisk: 65,
      proposedRisk: 55,
      volatility: {
        current: 12.5,
        proposed: 9.8
      },
      diversification: {
        current: 70,
        proposed: 90
      }
    },
    returnProjections: {
      current: {
        fiveYear: 6.5,
        tenYear: 7.2,
        twentyYear: 7.8
      },
      proposed: {
        fiveYear: 8.2,
        tenYear: 9.5,
        twentyYear: 10.8
      }
    }
  });
  
  // Calculate portfolio metrics whenever allocation changes
  useEffect(() => {
    const newPortfolioData = { ...portfolioData };
    
    // Calculate risk profile
    let currentRiskScore = 0;
    let proposedRiskScore = 0;
    let currentVolatility = 0;
    let proposedVolatility = 0;
    let currentReturnProjection = 0;
    let proposedReturnProjection = 0;
    
    // Calculate weighted risk and volatility scores
    Object.entries(portfolioData.currentPortfolio).forEach(([assetType, percentage]) => {
      const assetKey = assetType as keyof typeof assetRiskScores;
      currentRiskScore += (assetRiskScores[assetKey] * percentage) / 100;
      currentVolatility += (assetVolatilityScores[assetKey] * percentage) / 100;
      currentReturnProjection += (assetExpectedReturns[assetKey] * percentage) / 100;
    });
    
    Object.entries(portfolioData.proposedPortfolio).forEach(([assetType, percentage]) => {
      const assetKey = assetType as keyof typeof assetRiskScores;
      proposedRiskScore += (assetRiskScores[assetKey] * percentage) / 100;
      proposedVolatility += (assetVolatilityScores[assetKey] * percentage) / 100;
      proposedReturnProjection += (assetExpectedReturns[assetKey] * percentage) / 100;
    });
    
    // Calculate diversification score
    const currentDiversification = calculateDiversificationScore(portfolioData.currentPortfolio);
    const proposedDiversification = calculateDiversificationScore(portfolioData.proposedPortfolio);
    
    // Update risk profile
    newPortfolioData.riskProfile.currentRisk = Math.round(currentRiskScore);
    newPortfolioData.riskProfile.proposedRisk = Math.round(proposedRiskScore);
    newPortfolioData.riskProfile.volatility.current = parseFloat(currentVolatility.toFixed(1));
    newPortfolioData.riskProfile.volatility.proposed = parseFloat(proposedVolatility.toFixed(1));
    newPortfolioData.riskProfile.diversification.current = currentDiversification;
    newPortfolioData.riskProfile.diversification.proposed = proposedDiversification;
    
    // Update return projections
    // Simple compounding for different time horizons
    newPortfolioData.returnProjections.current.fiveYear = parseFloat((currentReturnProjection * 1.05).toFixed(1));
    newPortfolioData.returnProjections.current.tenYear = parseFloat((currentReturnProjection * 1.1).toFixed(1));
    newPortfolioData.returnProjections.current.twentyYear = parseFloat((currentReturnProjection * 1.2).toFixed(1));
    
    newPortfolioData.returnProjections.proposed.fiveYear = parseFloat((proposedReturnProjection * 1.05).toFixed(1));
    newPortfolioData.returnProjections.proposed.tenYear = parseFloat((proposedReturnProjection * 1.1).toFixed(1));
    newPortfolioData.returnProjections.proposed.twentyYear = parseFloat((proposedReturnProjection * 1.2).toFixed(1));
    
    setPortfolioData(newPortfolioData);
  }, [portfolioData.currentPortfolio, portfolioData.proposedPortfolio]);
  
  // Calculate diversification score based on distribution of assets
  const calculateDiversificationScore = (portfolio: Record<string, number>): number => {
    // Count non-zero allocations
    const activeAssets = Object.values(portfolio).filter(value => value > 0).length;
    
    // Calculate how evenly distributed the assets are
    const values = Object.values(portfolio).filter(value => value > 0);
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    
    // Higher number of assets and lower variance means better diversification
    const baseScore = Math.min(100, activeAssets * 20); // Up to 100 for 5 asset types
    const variancePenalty = Math.min(30, variance / 10); // Penalty for uneven distribution
    
    return Math.max(0, Math.min(100, baseScore - variancePenalty));
  };
  
  // Update portfolio allocation
  const handleAllocationChange = (
    portfolioType: 'currentPortfolio' | 'proposedPortfolio',
    assetType: string,
    value: number
  ) => {
    setPortfolioData(prev => ({
      ...prev,
      [portfolioType]: {
        ...prev[portfolioType],
        [assetType]: value
      }
    }));
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };
  
  // Colors for asset types
  const assetColors = {
    stocks: '#3b82f6', // blue
    bonds: '#8b5cf6', // purple
    realEstate: '#f59e0b', // amber
    savings: '#10b981', // emerald
    cosechaCapital: '#16a34a', // green
    other: '#6b7280' // gray
  };
  
  // Asset type labels
  const assetLabels = {
    stocks: 'Acciones',
    bonds: 'Bonos',
    realEstate: 'Bienes Raíces',
    savings: 'Ahorro',
    cosechaCapital: 'Cosecha Capital',
    other: 'Otros'
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <BarChart3 className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Optimización de Portafolio</h2>
              <p className="text-blue-100">Descubre cómo Cosecha Capital mejora tu portafolio de inversión</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 space-y-8">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="space-y-8"
          >
            {/* Portfolio Allocation */}
            <motion.div variants={itemVariants} className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-blue-600" />
                <span>Distribución de Activos</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Current Portfolio */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700">Portafolio Actual</h4>
                  
                  <div className="h-4 w-full bg-gray-200 rounded-full overflow-hidden">
                    {Object.entries(portfolioData.currentPortfolio).map(([assetType, percentage], index) => (
                      percentage > 0 && (
                        <div 
                          key={assetType}
                          className="h-full float-left transition-all duration-500"
                          style={{ 
                            width: `${percentage}%`, 
                            backgroundColor: assetColors[assetType as keyof typeof assetColors] 
                          }}
                          title={`${assetLabels[assetType as keyof typeof assetLabels]}: ${percentage}%`}
                        ></div>
                      )
                    ))}
                  </div>
                  
                  <div className="space-y-3">
                    {Object.entries(portfolioData.currentPortfolio).map(([assetType, percentage]) => (
                      <div key={assetType} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: assetColors[assetType as keyof typeof assetColors] }}
                            ></div>
                            <span className="text-sm font-medium text-gray-700">
                              {assetLabels[assetType as keyof typeof assetLabels]}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-700">{percentage}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={percentage}
                          onChange={(e) => handleAllocationChange('currentPortfolio', assetType, parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                          style={{
                            background: `linear-gradient(to right, ${assetColors[assetType as keyof typeof assetColors]} 0%, ${assetColors[assetType as keyof typeof assetColors]} ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Proposed Portfolio */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700">Portafolio Propuesto con Cosecha Capital</h4>
                  
                  <div className="h-4 w-full bg-gray-200 rounded-full overflow-hidden">
                    {Object.entries(portfolioData.proposedPortfolio).map(([assetType, percentage], index) => (
                      percentage > 0 && (
                        <div 
                          key={assetType}
                          className="h-full float-left transition-all duration-500"
                          style={{ 
                            width: `${percentage}%`, 
                            backgroundColor: assetColors[assetType as keyof typeof assetColors] 
                          }}
                          title={`${assetLabels[assetType as keyof typeof assetLabels]}: ${percentage}%`}
                        ></div>
                      )
                    ))}
                  </div>
                  
                  <div className="space-y-3">
                    {Object.entries(portfolioData.proposedPortfolio).map(([assetType, percentage]) => (
                      <div key={assetType} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: assetColors[assetType as keyof typeof assetColors] }}
                            ></div>
                            <span className="text-sm font-medium text-gray-700">
                              {assetLabels[assetType as keyof typeof assetLabels]}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-700">{percentage}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={percentage}
                          onChange={(e) => handleAllocationChange('proposedPortfolio', assetType, parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                          style={{
                            background: `linear-gradient(to right, ${assetColors[assetType as keyof typeof assetColors]} 0%, ${assetColors[assetType as keyof typeof assetColors]} ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Portfolio Metrics */}
            <motion.div variants={itemVariants} className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <span>Métricas de Portafolio</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Risk Profile */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-200 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-blue-700" />
                    </div>
                    <h4 className="font-medium text-blue-800">Perfil de Riesgo</h4>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-blue-700">Actual</span>
                        <span className="text-blue-700">{portfolioData.riskProfile.currentRisk}/100</span>
                      </div>
                      <div className="h-2 w-full bg-blue-200 rounded-full">
                        <div 
                          className="h-full bg-blue-600 rounded-full"
                          style={{ width: `${portfolioData.riskProfile.currentRisk}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-blue-700">Propuesto</span>
                        <span className="text-blue-700">{portfolioData.riskProfile.proposedRisk}/100</span>
                      </div>
                      <div className="h-2 w-full bg-blue-200 rounded-full">
                        <div 
                          className="h-full bg-green-600 rounded-full"
                          style={{ width: `${portfolioData.riskProfile.proposedRisk}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Volatility */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-200 rounded-lg flex items-center justify-center">
                      <Zap className="w-5 h-5 text-purple-700" />
                    </div>
                    <h4 className="font-medium text-purple-800">Volatilidad</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-purple-700">Actual</span>
                      <span className="text-lg font-bold text-purple-700">{portfolioData.riskProfile.volatility.current}%</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-purple-700">Propuesto</span>
                      <span className="text-lg font-bold text-green-700">{portfolioData.riskProfile.volatility.proposed}%</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-purple-700">Reducción</span>
                      <span className="text-lg font-bold text-green-700">
                        {((portfolioData.riskProfile.volatility.current - portfolioData.riskProfile.volatility.proposed) / portfolioData.riskProfile.volatility.current * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Diversification */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-200 rounded-lg flex items-center justify-center">
                      <PieChart className="w-5 h-5 text-green-700" />
                    </div>
                    <h4 className="font-medium text-green-800">Diversificación</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-green-700">Actual</span>
                      <span className="text-lg font-bold text-green-700">{portfolioData.riskProfile.diversification.current}/100</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-green-700">Propuesto</span>
                      <span className="text-lg font-bold text-green-700">{portfolioData.riskProfile.diversification.proposed}/100</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-green-700">Mejora</span>
                      <span className="text-lg font-bold text-green-700">
                        {((portfolioData.riskProfile.diversification.proposed - portfolioData.riskProfile.diversification.current) / portfolioData.riskProfile.diversification.current * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {/* Return Projections */}
            <motion.div variants={itemVariants} className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <span>Proyecciones de Rendimiento</span>
              </h3>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Horizonte
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Portafolio Actual
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Con Cosecha Capital
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Diferencia
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        5 Años
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                        {portfolioData.returnProjections.current.fiveYear}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600 font-medium">
                        {portfolioData.returnProjections.proposed.fiveYear}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600 font-medium">
                        +{(portfolioData.returnProjections.proposed.fiveYear - portfolioData.returnProjections.current.fiveYear).toFixed(1)}%
                      </td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        10 Años
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                        {portfolioData.returnProjections.current.tenYear}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600 font-medium">
                        {portfolioData.returnProjections.proposed.tenYear}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600 font-medium">
                        +{(portfolioData.returnProjections.proposed.tenYear - portfolioData.returnProjections.current.tenYear).toFixed(1)}%
                      </td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        20 Años
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                        {portfolioData.returnProjections.current.twentyYear}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600 font-medium">
                        {portfolioData.returnProjections.proposed.twentyYear}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600 font-medium">
                        +{(portfolioData.returnProjections.proposed.twentyYear - portfolioData.returnProjections.current.twentyYear).toFixed(1)}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Continue Button */}
          <div className="flex justify-end">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onContinue}
              className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <span>Continuar</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PortfolioComparison;